import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useContent } from "../../context/ContentContext";
import { applyTheme } from "../ThemeProvider";
import { Save, RotateCcw, Palette, Type } from "lucide-react";
import { toast } from "sonner";

const HEADING_FONTS = [
  "Playfair Display",
  "Cormorant Garamond",
  "Bodoni Moda",
  "DM Serif Display",
  "Fraunces",
  "Gloock",
  "Spectral",
  "Marcellus",
];

const BODY_FONTS = [
  "Outfit",
  "Manrope",
  "Work Sans",
  "Figtree",
  "Jost",
  "Lato",
  "Karla",
  "IBM Plex Sans",
];

const COLOR_FIELDS = [
  { key: "gold", label: "Primary Accent (Gold)" },
  { key: "coral", label: "Secondary Accent (Coral)" },
  { key: "void", label: "Background" },
  { key: "surface", label: "Cards / Surface" },
  { key: "ink", label: "Primary Text" },
  { key: "smoke", label: "Secondary Text" },
];

const DEFAULT_THEME = {
  colors: { gold: "#FFC800", coral: "#FF5252", void: "#050505", surface: "#121212", ink: "#F5F5F5", smoke: "#A3A3A3" },
  fonts: { heading: "Playfair Display", body: "Outfit" },
};

export const ThemeEditor = () => {
  const { content, setContent, fetchContent } = useContent();
  const [theme, setTheme] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content?.theme) setTheme(JSON.parse(JSON.stringify(content.theme)));
  }, [content]);

  if (!theme) return <div className="text-smoke animate-pulse">Loading...</div>;

  // Live preview whenever theme changes
  const updateColor = (key, value) => {
    const next = { ...theme, colors: { ...theme.colors, [key]: value } };
    setTheme(next);
    applyTheme(next);
  };

  const updateFont = (key, value) => {
    const next = { ...theme, fonts: { ...theme.fonts, [key]: value } };
    setTheme(next);
    applyTheme(next);
  };

  const resetDefault = () => {
    const def = JSON.parse(JSON.stringify(DEFAULT_THEME));
    setTheme(def);
    applyTheme(def);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { content: { theme } });
      setContent({ ...content, theme });
      await fetchContent();
      toast.success("Theme saved!", { description: "Your website style has been updated." });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="theme-editor">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h2 className="font-display text-3xl text-ink">Theme</h2>
          <p className="text-sm text-smoke mt-1">Customize colors and fonts. Changes preview live.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={resetDefault}
            data-testid="reset-theme-btn"
            className="flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm text-smoke transition-colors hover:border-coral hover:text-coral"
          >
            <RotateCcw size={15} /> Reset
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-theme-btn"
            className="flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-sm font-semibold uppercase tracking-widest text-black transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
          >
            <Save size={16} /> {saving ? "Saving..." : "Save Theme"}
          </button>
        </div>
      </div>

      {/* Colors */}
      <div className="rounded-xl border border-line bg-surface p-6 mb-6">
        <h3 className="font-display text-xl text-gold mb-5 flex items-center gap-2">
          <Palette size={20} /> Colors
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          {COLOR_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center justify-between rounded-lg border border-[#2a2a2a] bg-void p-4">
              <div>
                <span className="block text-sm text-ink">{f.label}</span>
                <span className="block text-xs text-smoke uppercase tracking-wider">{theme.colors[f.key]}</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={theme.colors[f.key]}
                  onChange={(e) => updateColor(f.key, e.target.value)}
                  data-testid={`color-text-${f.key}`}
                  className="w-24 rounded-md border border-[#2a2a2a] bg-void px-2 py-1.5 text-xs text-ink outline-none focus:border-gold"
                />
                <input
                  type="color"
                  value={theme.colors[f.key]}
                  onChange={(e) => updateColor(f.key, e.target.value)}
                  data-testid={`color-picker-${f.key}`}
                  className="h-10 w-12 cursor-pointer rounded-md border border-[#2a2a2a] bg-transparent"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fonts */}
      <div className="rounded-xl border border-line bg-surface p-6">
        <h3 className="font-display text-xl text-gold mb-5 flex items-center gap-2">
          <Type size={20} /> Fonts
        </h3>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-smoke">Heading Font</label>
            <select
              value={theme.fonts.heading}
              onChange={(e) => updateFont("heading", e.target.value)}
              data-testid="font-heading-select"
              className="w-full rounded-lg border border-[#2a2a2a] bg-void px-4 py-2.5 text-sm text-ink outline-none focus:border-gold"
            >
              {HEADING_FONTS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <p className="mt-3 font-display text-3xl text-ink">Mixing Dreams</p>
          </div>
          <div>
            <label className="mb-2 block text-xs uppercase tracking-widest text-smoke">Body Font</label>
            <select
              value={theme.fonts.body}
              onChange={(e) => updateFont("body", e.target.value)}
              data-testid="font-body-select"
              className="w-full rounded-lg border border-[#2a2a2a] bg-void px-4 py-2.5 text-sm text-ink outline-none focus:border-gold"
            >
              {BODY_FONTS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
            <p className="mt-3 font-body text-base text-smoke">
              Crafting experiences, one pour at a time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
